// Hideo - script editor's writing surface (#scriptPageWrap: #scriptBody, #blockHandle, #blockDrop, static markup in
// index.html that ScriptEditor.vue moves into the page; the floating #slashMenu, #selMenu, #blockMenu, #linkBubble are
// static too). Vue never draws inside them: the contenteditable page is driven by these modules, ported from the legacy
// editor/editor.js (removed in wave 12):
//   core.ts       the page, the kept selection (savedRange), the caret, ex()
//   commands.ts   lists and checklists, colours, size, line spacing, links, block types, fonts, tbState
//   slash.ts      the "/" menu and "@" in the page
//   selmenu.ts    the selection menu and the selection's colours
//   clipboard.ts  paste, copy, cut
//   links.ts      the link bubble
//   typing.ts     keys, Markdown shortcuts, automatic links, clicks on links / comments / checklist boxes
//   blocks.ts     block handle, drag (Alt = copy), block menu, Ctrl+D
//   floating.ts   tap elsewhere / Esc closes the floating elements, edHideFloating, the toolbar follows the cursor
// installEditorSurface() adds the listeners the legacy file added when the bundle loaded, in the same order (the
// document and window capture-phase ones keep their place among the others: docs/architecture.md), and the ones that
// replace the inline handlers of the page and of the floating menus (the menus are drawn as HTML strings whose buttons
// carry data-act / data-pick; each element's listener looks at what was hit). src/main.ts calls it once, right after
// installMentions and before the Vue app is mounted.
import { edBodyEl } from '../html'
import { installSlash } from './slash'
import { installSelMenu } from './selmenu'
import { installKeys, installInput } from './typing'
import { installClipboard } from './clipboard'
import { installBlocks } from './blocks'
import { installLinkBubble } from './links'
import { installFloating, installToolbarSync } from './floating'
import { installPages } from './pages'

let installed = false
export function installEditorSurface(doc: Pick<Document, 'addEventListener'> = document, win: Pick<Window, 'addEventListener'> = window): void {
  if (installed) return
  installed = true
  const body = edBodyEl()
  installSlash()                // #slashMenu mousedown
  installSelMenu(doc, win)      // #selMenu pointerdown, mousedown, change; document selectionchange ×2, window resize
  installKeys(body)             // #scriptBody keydown, click, mousedown
  installClipboard(body)        // #scriptBody paste, copy, cut
  installInput(body)            // #scriptBody input ×2 (queueScriptSave + slashCheck first, then onInput)
  installBlocks(body, win)      // #scriptBody mousemove, #scriptPageWrap mouseleave, #blockGrip pointer…, window Alt (capture),
                                // "+" mousedown and click, #blockMenu mousedown and click
  installLinkBubble()           // #linkBubble mousedown, click
  installFloating(doc, win)     // document pointerdown (capture), window Esc (capture), window resize
  installToolbarSync(doc)       // document selectionchange, document pointerdown (capture)
  installPages(body)            // sheets layer behind the page, ResizeObserver and MutationObserver (no listeners)
}
/** For the golden tests: installs again (on other targets) */
export const _resetSurfaceInstall = () => { installed = false }
